import './InputText.css';
import Label from '../Label';
import { forwardRef, HTMLAttributes, InputHTMLAttributes, ReactElement, ReactNode, useState } from 'react';
import React from 'react';
import { IToolTipProps } from '../ToolTip/ToolTip';
import SuccessIcon from './SuccessIcon/SuccessIcon';
import { addUnderscores } from '../../utils/utils';
import DynamicContainer from '../../foundational/Layout/DynamicContainer';

export interface IInputTextProps extends HTMLAttributes<HTMLInputElement> {
    label?: ReactNode;
    tooltip?: string | ReactElement | ReactElement[];
    tooltipNotchPosition?: IToolTipProps['notchPosition']
    mobileTooltipNotchPosition?: IToolTipProps['mobileNotchPosition']
    value?: string | number;
    setValue?: React.Dispatch<React.SetStateAction<any>>;
    onChangeCallback?: (((value: React.ChangeEvent<HTMLInputElement>) => void) & React.ChangeEventHandler<any>);
    onFocusCallback?: (((value: React.FocusEvent<HTMLInputElement>) => void) & React.FocusEventHandler<any>);
    onBlurCallback?: (((value: React.FocusEvent<HTMLInputElement>) => void) & React.FocusEventHandler<any>);
    required?: boolean;
    state?: 'neutral' | 'success' | 'error';
    errorMessage?: ReactNode;
    additionalText?: ReactNode;
    className?: string;
    disabled?: boolean
    containerId?: string;
    useSuccessIcon?: boolean;
    helperText?: string;
}

const InputText = forwardRef(
    (
        {
            label,
            tooltip,
            tooltipNotchPosition,
            mobileTooltipNotchPosition,
            onChangeCallback = () => { },
            onFocusCallback = () => { },
            onBlurCallback = () => { },
            setValue = () => { },
            state = 'neutral',
            errorMessage = '',
            additionalText = '',
            required,
            className = '',
            disabled = false,
            value,
            containerId,
            useSuccessIcon = true,
            helperText,
            ...rest
        }: IInputTextProps & InputHTMLAttributes<any>, ref: any): JSX.Element => {

        return (
            <div className='InputText__container' {...(containerId && { id: containerId })}>
                <label className={`InputText`}>
                    {label && (
                        <Label
                            error={state == 'error'}
                            tooltip={tooltip}
                            tooltipAlign={tooltipNotchPosition}
                            mobileTooltipAlign={mobileTooltipNotchPosition}
                            required={required}
                            alternate={true}
                            helperText={helperText}
                        >
                            {label}
                        </Label>
                    )}
                    <div
                        className={`InputText__inputWrapper${state == 'error' ? '--error' : ''}  ${disabled ? 'InputText__inputWrapper--disabled' : ''} ${errorMessage ? '' : 'mb-6'}`}
                    >
                        <input
                            className={`InputText__input${state == 'error' ? ' InputText__input--error' : ''} ${className} ${disabled ? 'InputText__input--disabled' : ''}`}
                            onFocus={onFocusCallback}
                            onBlur={onBlurCallback}
                            aria-label={typeof label === 'string' ? label : ('aria-label' in rest ? rest['aria-label'] : '')}
                            aria-invalid={state === 'error' ? true : false}
                            aria-disabled={disabled ? true : false}
                            disabled={disabled ? true : false}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setValue(e.target.value);
                                onChangeCallback(e);
                            }}
                            value={value}
                            ref={ref}
                            {...rest}
                        />
                    </div>
                    {errorMessage && <p
                        className={`InputText__description ${state === 'error' ? 'InputText__errorMessage' : ''}`}
                        id={`${addUnderscores(rest.id, rest.name)}__input__adv__helper`}
                    >
                        {state === 'error' ? errorMessage : ''}
                    </p>}
                    {additionalText && (
                        <DynamicContainer displayTag={typeof additionalText === 'string'} tag='p'
                            className={`InputText__note`}
                        >
                            {additionalText}
                        </DynamicContainer>
                    )}
                </label>
                {useSuccessIcon && <SuccessIcon showIcon={state === 'success'} />}
            </div>
        );
    }
)

InputText.displayName = 'InputText';

export default InputText;